import React from 'react';
import { cn } from '@/app/_module/lib/utils';

/**
 * globals.css strips every focus outline with !important, so controls on this
 * page put one back (layered !important beats the unlayered rule).
 */
export const focusRing =
  'focus-visible:!outline-solid focus-visible:!outline-2 focus-visible:outline-offset-2 focus-visible:!outline-primary';

/** Form control border: the lightest grey that still clears 3:1 against white. */
export const controlBorder = 'border-neutral-500/80';

// The app's display serif (ComingSoonPanel, forum), used for the one headline per screen.
export const displayFont: React.CSSProperties = {
  fontFamily: 'var(--font-fraunces), Georgia, serif',
  fontVariationSettings: '"SOFT" 40',
};

export type Tone = 'ok' | 'waiting' | 'blocked' | 'neutral';

// Dots are >= 3:1 and text >= 4.5:1 on white. A dot never stands alone: it
// always sits next to a written label.
const TONES: Record<Tone, { dot: string; text: string; wash: string }> = {
  ok: { dot: 'bg-emerald-600 ring-emerald-600/15', text: 'text-emerald-700', wash: 'from-emerald-50' },
  waiting: { dot: 'bg-amber-600 ring-amber-500/20', text: 'text-amber-700', wash: 'from-amber-50' },
  blocked: { dot: 'bg-red-600 ring-red-600/15', text: 'text-red-700', wash: 'from-red-50' },
  neutral: { dot: 'bg-neutral-500 ring-neutral-400/20', text: 'text-neutral-600', wash: 'from-neutral-50' },
};

export const toneWash = (tone: Tone) => TONES[tone].wash;

export function StatusDot({ tone, className }: { tone: Tone; className?: string }) {
  return <span aria-hidden className={cn('inline-block size-2 shrink-0 rounded-full ring-4', TONES[tone].dot, className)} />;
}

export function StatusLabel({ tone, children, className }: { tone: Tone; children: React.ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-2.5 text-[13px] font-medium leading-none', TONES[tone].text, className)}>
      <StatusDot tone={tone} />
      {children}
    </span>
  );
}

/** A titled block below the status panel. Flat on purpose: only the status panel lifts. */
export function Section({
  id,
  title,
  description,
  aside,
  children,
}: {
  id: string;
  title: string;
  description?: React.ReactNode;
  aside?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          {/* `!` because globals.css resizes every h1-h4 below 767px */}
          <h3 id={id} className="!text-[15px] font-semibold tracking-[-0.005em] text-neutral-900">
            {title}
          </h3>
          {description && <p className="mt-1 text-pretty text-sm leading-relaxed text-neutral-500">{description}</p>}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function Panel({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn('rounded-xl border border-neutral-200/80 bg-white', className)}>{children}</div>;
}

export interface Fact {
  label: string;
  value: React.ReactNode;
  note?: string;
  /** Long values (business names) take the full row on phones. */
  wide?: boolean;
}

export function FactGrid({ facts, className }: { facts: Fact[]; className?: string }) {
  return (
    <dl className={cn('grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3', className)}>
      {facts.map((f) => (
        <div key={f.label} className={cn('min-w-0', f.wide && 'col-span-2 sm:col-span-1')}>
          <dt className="text-xs text-neutral-500">{f.label}</dt>
          <dd className="mt-1 break-words text-sm font-medium tabular-nums text-neutral-900">{f.value}</dd>
          {f.note && <dd className="mt-0.5 text-xs text-neutral-500">{f.note}</dd>}
        </div>
      ))}
    </dl>
  );
}

/** Warnings that carry real risk (texting stops, a code expires). Text first, colour second. */
export function Notice({
  tone = 'waiting',
  icon: Icon,
  children,
}: {
  tone?: 'waiting' | 'blocked';
  icon?: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex gap-3 rounded-lg px-3.5 py-3 text-sm leading-relaxed',
        tone === 'waiting' ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-800',
      )}
    >
      {Icon && <Icon aria-hidden className="mt-0.5 size-4 shrink-0" />}
      <div className="min-w-0 [&_strong]:font-semibold">{children}</div>
    </div>
  );
}

export const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
